import React from "react";
import { useParams, Link } from "react-router-dom";
import bg_image from "../../assets/images/comparison_bg.svg";
import vs_image from "../../assets/images/vs_img.svg";
import Button from "../../components/Button";
import homeIcon from "../../assets/images/homeicon_compare.svg";
import OnThisPage from "../../components/OnThisPage";
import COMPARISON_DATA from "../../utils/ComparisonData";
import { openInNewTab } from "../../utils/util";

export default function ComparisonPage() {
  const { slug } = useParams(); // Get the slug from the URL

  // Find the comparison data based on the slug
  const comparison = COMPARISON_DATA.comparisons.find(
    (comp) => comp.slug === slug
  );

  if (!comparison) {
    return <div>Comparison not found.</div>;
  }

  const { first_exchange, second_exchange, services, key_difference, second_para, features, parameter_comparisons, user_feedback, final_verdict, final_thoughts, first_exchange_img, second_exchange_img, first_exchange_link, second_exchange_link } = comparison;

  return (
    <div className="mt-[50px] mx-auto sm:px-0">
      <OnThisPage classes="mt-[30px] mb-[10px]" />

      {/* Hero Section */}
      <div className="bg-cover bg-center text-white p-6 md:p-12" style={{ backgroundImage: `url(${bg_image})` }}>
        <div className="flex items-center text-sm md:text-base font-semibold">
          <img src={homeIcon} alt="home-icon" className="mr-2" />
          <span>/ Comparison / {first_exchange} vs {second_exchange}</span>
        </div>

        <div className="flex justify-center gap-4 my-8">
          {[first_exchange_img, vs_image, second_exchange_img].map((img, i) => (
            <img key={i} src={img} alt="/image" className={`w-24 h-20 p-2 md:w-32 md:h-28 md:p-5 ${i === 1 ? "w-8 md:w-12" : "bg-white rounded-lg"}`} />
          ))}
        </div>

        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl md:text-4xl font-bold mb-6">{services}</h1>
          {[key_difference, second_para].map((text, i) => (
            <p key={i} className="text-sm md:text-base mb-4 lg:font-[600]">{text}</p>
          ))}
        </div>

        <div className="mt-8 max-w-lg mx-auto grid gap-4 bg-white p-[30px] rounded-lg">
          {[{ text: first_exchange, link: first_exchange_link }, { text: second_exchange, link: second_exchange_link }].map(({ text, link }, i) => (
            <Link key={i}>
              <Button
                btnText={`Visit ${text}`}
                onClick={() => openInNewTab(link)}
                classes={`w-full ${i === 0 ? "bg-[#7B3BCC] border border-white text-white" : "bg-white border border-[#7B3BCC] text-[#7B3BCC]"} font-bold py-3 text-sm md:text-base`}
              />
            </Link>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-12 mx-6 sm:mx-20">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">How Do {second_exchange} and {first_exchange} Compare?</h2>
        <p className="text-[#888888] text-base md:text-lg mb-8 max-w-3xl lg:max-w-full lg:font-[600]">
          Both {second_exchange} and {first_exchange} are trusted money transfer platforms, but they differ in pricing, delivery speed, payout options, and global reach. Below, we break down the major distinctions.
        </p>

        {/* Features Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                {["Feature", first_exchange, second_exchange].map((header, i) => (
                  <th key={i} className="py-3 px-4 text-left text-sm font-semibold text-gray-700 lg:text-[16px]">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map(({ feature, [first_exchange]: firstValue, [second_exchange]: secondValue }, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  {[feature, firstValue, secondValue].map((val, j) => (
                    <td key={j} className="py-3 px-4 text-sm text-gray-700 lg:font-[600]">{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Key Comparisons & Feedback */}
        <div className="mt-12 grid gap-8">
          {parameter_comparisons.map(({ title, [first_exchange]: firstValue, [second_exchange]: secondValue }, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-0">
              <h3 className="text-xl font-bold mb-4 md:text-3xl bg-gray-50 p-4">{title}</h3>
              <ul className="list-disc list-inside space-y-4 p-4">
                {[{ text: first_exchange, value: firstValue }, { text: second_exchange, value: secondValue }].map(({ text, value }, j) => (
                  <li key={j} className="rounded-lg font-bold">{text} <span className="font-[400]">{value}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Final Verdict & Thoughts */}
      {["Final Verdict", "Final Thoughts"].map((section, i) => (
        <div key={i} className={`mt-12 ${i === 1 ? "bg-[#F9F9F9] p-6 sm:px-20" : "rounded-lg sm:mx-20 mx-6"}`}>
          <h2 className="text-2xl font-bold mb-6 md:text-3xl">{section}</h2>
          {i === 0 ? (
            <div className="overflow-x-auto rounded-lg">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    {["Category", "Best Choice"].map((header, j) => (
                      <th key={j} className="py-3 px-4 text-left text-sm font-semibold text-gray-700 lg:text-[16px]">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(final_verdict).map(([key, value], j) => (
                    <tr key={j} className={j % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="py-3 px-4 text-sm text-gray-700 lg:text-[16px] lg:font-[500]">{key}</td>
                      <td className="py-3 px-4 text-sm font-bold lg:text-[16px] lg:font-[500]">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              {[final_thoughts[first_exchange], final_thoughts[second_exchange]].map((text, j) => (
                <li key={j}>{text}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
